import { useState, useEffect, useCallback } from 'react';
import {
    Box, Flex, Heading, HStack, VStack, Text, IconButton, Button,
    Grid, GridItem, Badge, Spinner, useDisclosure, useToast, useBreakpointValue,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
    ModalCloseButton, ModalFooter, Input, InputGroup, InputLeftElement,
    Divider, Center
} from '@chakra-ui/react';
import { FiChevronLeft, FiChevronRight, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { fetchAllPages } from '../services/fetchAll';
import LeaveBatchModal from '../components/LeaveBatchModal';
import { useWebSocket } from '../context/WebSocketContext';
import LeaveApplicationModal from '../components/LeaveApplicationModal';
import MobileMonthCalendar from '../mobile/components/MobileMonthCalendar';

// ── 工具函式 ──────────────────────────────────────────────
const getTaipeiToday = () =>
    new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei' }).format(new Date());

const toDateStr = (year, month, day) =>
    `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];

function buildCalendarCells(year, month) {
    const firstDay = new Date(year, month - 1, 1);
    const daysInMonth = new Date(year, month, 0).getDate();
    const offset = firstDay.getDay();
    const cells = Array(offset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
        const dow = new Date(year, month - 1, d).getDay();
        cells.push({ day: d, dateStr: toDateStr(year, month, d), isWeekend: dow === 0 || dow === 6 });
    }
    return cells;
}

function groupLeavesByDate(leaves) {
    const map = {};
    leaves.forEach(l => {
        if (!map[l.date]) map[l.date] = { sick: [], personal: [], other: [] };
        if (l.type === 'SICK') map[l.date].sick.push(l);
        else if (l.type === 'PERSONAL') map[l.date].personal.push(l);
        else map[l.date].other.push(l);
    });
    return map;
}

const TYPE_LABEL = { SICK: '病假', PERSONAL: '事假', OTHER: '其他' };
const TYPE_COLOR = { SICK: 'red', PERSONAL: 'blue', OTHER: 'gray' };

// ── 主元件 ──────────────────────────────────────────────
const LeavesPage = () => {
    const toast = useToast();
    const { lastMessage } = useWebSocket();
    const isMobile = useBreakpointValue({ base: true, md: false });
    const today = getTaipeiToday();
    const [now] = useState(() => new Date());
    const [viewYear, setViewYear] = useState(now.getFullYear());
    const [viewMonth, setViewMonth] = useState(now.getMonth() + 1);

    const [monthLeaves, setMonthLeaves] = useState([]);
    const [leaveMap, setLeaveMap] = useState({});
    const [loadingMonth, setLoadingMonth] = useState(false);

    const cells = buildCalendarCells(viewYear, viewMonth);

    // 單日 Modal
    const { isOpen: isDayOpen, onOpen: openDay, onClose: closeDay } = useDisclosure();
    const [dayTarget, setDayTarget] = useState(null);
    const [dayLeaves, setDayLeaves] = useState([]);

    // 搜尋 Modal
    const { isOpen: isSearchOpen, onOpen: openSearch, onClose: closeSearch } = useDisclosure();
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    // 登記請假 Modal
    const { isOpen: isAddOpen, onOpen: openAdd, onClose: closeAdd } = useDisclosure();
    const [addDefaultDate, setAddDefaultDate] = useState('');

    // 多日選取
    const [selectedDates, setSelectedDates] = useState(new Set());

    // 批量請假 Modal
    const { isOpen: isBatchOpen, onOpen: openBatch, onClose: closeBatch } = useDisclosure();

    const prevMonth = () => {
        if (viewMonth === 1) { setViewYear(y => y - 1); setViewMonth(12); }
        else setViewMonth(m => m - 1);
        setSelectedDates(new Set());
    };
    const nextMonth = () => {
        if (viewMonth === 12) { setViewYear(y => y + 1); setViewMonth(1); }
        else setViewMonth(m => m + 1);
        setSelectedDates(new Set());
    };

    const fetchMonthLeaves = useCallback(async () => {
        setLoadingMonth(true);
        try {
            // [Hotfix 2026-07-27，2026-08-01 改用共用 fetchAllPages util] 原本只帶
            // start_date（無 end_date）+ limit 500：後端依日期遞增排序後截斷，導致
            // 「月底幾天的請假在月曆上完全不見」（實測 07/01–07/26 已有 521 筆 → 07/24
            // 只顯示一部分、07/27 起全部消失；整個七月 608 筆、還會被 8 月之後的 190 筆
            // 一起吃掉 limit）。改為：① 明確帶 end_date 把查詢框在當月 ② 用
            // fetchAllPages 分頁抓到「回傳筆數 < 一頁」為止，不再依賴猜一個夠大的 limit
            // （見 src/services/fetchAll.js 的說明）。
            const firstOfMonth = toDateStr(viewYear, viewMonth, 1);
            const lastDay = new Date(viewYear, viewMonth, 0).getDate();
            const lastOfMonth = toDateStr(viewYear, viewMonth, lastDay);

            const collected = await fetchAllPages(({ skip, limit }) => authAPI.leaves.getLeaves({
                start_date: firstOfMonth,
                end_date: lastOfMonth,
                skip,
                limit,
            }));

            const monthStr = `${viewYear}-${String(viewMonth).padStart(2, '0')}`;
            const filtered = collected.filter(l => l.date && l.date.startsWith(monthStr));
            setMonthLeaves(filtered);
            setLeaveMap(groupLeavesByDate(filtered));
        } catch (e) {
            console.error(e);
            toast({ title: '無法載入請假資料', status: 'error' });
        } finally {
            setLoadingMonth(false);
        }
    }, [viewYear, viewMonth]);

    useEffect(() => { fetchMonthLeaves(); }, [fetchMonthLeaves]);

    useEffect(() => {
        if (lastMessage?.type === 'STUDENT_STATUS_UPDATE') fetchMonthLeaves();
    }, [lastMessage]);

    // 圓圈選取（多日）
    const toggleDateSelection = (e, dateStr) => {
        e.stopPropagation();
        setSelectedDates(prev => {
            const next = new Set(prev);
            next.has(dateStr) ? next.delete(dateStr) : next.add(dateStr);
            return next;
        });
    };

    // 點擊日期格
    const handleCellClick = (dateStr) => {
        setDayTarget(dateStr);
        const d = leaveMap[dateStr] || { sick: [], personal: [], other: [] };
        setDayLeaves([...d.sick, ...d.personal, ...d.other]);
        openDay();
    };

    // 登記請假（從日期格點進來預帶日期）
    const handleOpenAddFromDay = () => {
        setAddDefaultDate(dayTarget || '');
        closeDay();
        openAdd();
    };

    const handleOpenAddGlobal = () => {
        setAddDefaultDate('');
        openAdd();
    };

    // 刪除請假
    const handleDelete = async (id) => {
        if (!window.confirm('確定要取消這筆請假嗎？')) return;
        try {
            await authAPI.leaves.deleteLeave(id);
            toast({ title: '已取消請假', status: 'success', duration: 2000 });
            fetchMonthLeaves();
            // 更新 dayLeaves（移除該筆）
            setDayLeaves(prev => prev.filter(l => l.id !== id));
        } catch (e) {
            toast({ title: '取消失敗', status: 'error' });
        }
    };

    // 搜尋學生
    const handleSearch = async () => {
        if (!searchInput.trim()) return;
        setSearchLoading(true);
        try {
            // [Hotfix 2026-08-01] 原本 limit:100、不帶日期範圍，後端又是 date 遞增排序，
            // 搜熱門姓氏（如「陳」256 筆、「黃」167 筆）時只拿得到最舊的 100 筆——查最近
            // 幾個月的請假反而查不到，全名搜尋因為單人最多 44 筆 < 100 才沒被發現。
            // 改用 fetchAllPages 分頁抓完，姓氏搜尋不再有隱形筆數上限。
            const collected = await fetchAllPages(
                ({ skip, limit }) => authAPI.leaves.getLeaves({ search: searchInput.trim(), skip, limit })
            );
            setSearchResults(collected);
        } catch (e) {
            toast({ title: '搜尋失敗', status: 'error' });
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSearchDelete = async (id) => {
        if (!window.confirm('確定要取消這筆請假嗎？')) return;
        try {
            await authAPI.leaves.deleteLeave(id);
            toast({ title: '已取消請假', status: 'success', duration: 2000 });
            setSearchResults(prev => prev.filter(l => l.id !== id));
            fetchMonthLeaves();
        } catch (e) {
            toast({ title: '取消失敗', status: 'error' });
        }
    };

    const handleLeaveSuccess = () => {
        fetchMonthLeaves();
        closeAdd();
    };

    const numRows = Math.ceil(cells.length / 7);

    return (
        <Box h="100%" display="flex" flexDirection="column" px={{ base: 3, md: 4 }} pt={{ base: 3, md: 6 }} pb={3}>
            {/* 標題 + 月份導覽 */}
            <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" mb={4} gap={2}>
                <HStack spacing={{ base: 1.5, md: 3 }}>
                    <Heading size="lg" color="brand.800" display={{ base: 'none', md: 'block' }}>請假管理</Heading>
                    <Button leftIcon={<FiSearch />} variant="outline" size={{ base: 'xs', md: 'sm' }} onClick={openSearch}>
                        <Box as="span" display={{ base: 'none', md: 'inline' }}>搜尋學生</Box>
                    </Button>
                    <Button leftIcon={<FiPlus />} colorScheme="brand" size={{ base: 'xs', md: 'sm' }} onClick={handleOpenAddGlobal}>
                        <Box as="span" display={{ base: 'none', md: 'inline' }}>登記請假</Box>
                    </Button>
                </HStack>
                {!isMobile && (
                    <HStack
                        bg="white" border="1px solid" borderColor="gray.200"
                        borderRadius="lg" boxShadow="xs" px={3} py={1} justify="center"
                    >
                        <IconButton icon={<FiChevronLeft />} onClick={prevMonth} size="sm" variant="ghost" aria-label="上個月" />
                        <Text fontWeight="bold" minW="110px" textAlign="center">
                            {viewYear}年 {viewMonth}月
                            {loadingMonth && <Spinner size="xs" ml={2} color="red.400" />}
                        </Text>
                        <IconButton icon={<FiChevronRight />} onClick={nextMonth} size="sm" variant="ghost" aria-label="下個月" />
                    </HStack>
                )}
            </Flex>

            {isMobile ? (
                <Box flex="1" minH={0} overflowY="auto">
                    <MobileMonthCalendar
                        year={viewYear} month={viewMonth} today={today} loading={loadingMonth}
                        onPrevMonth={prevMonth} onNextMonth={nextMonth}
                        onDayClick={handleCellClick}
                        accentColor="red" rowHeight="63px"
                        getCellStyle={(dateStr) => {
                            const data = leaveMap[dateStr] || { sick: [], personal: [], other: [] };
                            const hasLeaves = data.sick.length + data.personal.length + data.other.length > 0;
                            if (data.sick.length > 0) return { bg: 'red.50' };
                            if (hasLeaves) return { bg: 'purple.50', todayColor: 'purple' };
                            return undefined;
                        }}
                        renderDay={(dateStr) => {
                            const data = leaveMap[dateStr] || { sick: [], personal: [], other: [] };
                            const total = data.sick.length + data.personal.length + data.other.length;
                            const isSelected = selectedDates.has(dateStr);
                            return (
                                <>
                                    <Box
                                        pos="absolute" top="3px" right="3px"
                                        w="14px" h="14px" borderRadius="full"
                                        border="2px solid"
                                        borderColor={isSelected ? 'red.500' : 'gray.300'}
                                        bg={isSelected ? 'red.500' : 'transparent'}
                                        cursor="pointer"
                                        onClick={(e) => toggleDateSelection(e, dateStr)}
                                        zIndex={1}
                                    />
                                    {total > 0 && (
                                        <Text pos="absolute" bottom="4px" left="0" right="0" textAlign="center"
                                            fontSize="sm" fontWeight="bold" color={data.sick.length > 0 ? 'red.600' : 'purple.600'}>
                                            {total}
                                        </Text>
                                    )}
                                </>
                            );
                        }}
                    />
                    {/* 圖例：文字直接壓在外殼畫布上（沒有卡片承載），所以用 --fg-*。
                        本頁在老師 PWA 也會渲染，那邊畫布是霧灰橄欖，gray.500 只有 2.29 對比、不達 AA；
                        下面桌面分支的同一份圖例早就是 var(--fg-muted) 了，這裡是漏改的那一半。 */}
                    <Box mt={3} px={1}>
                        <HStack spacing={4} flexWrap="wrap" gap={2}>
                            <HStack spacing={1.5}>
                                <Box w="12px" h="12px" borderRadius="sm" bg="red.50" border="1px solid" borderColor="red.200" />
                                <Text fontSize="xs" color="var(--fg-muted)">有病假</Text>
                            </HStack>
                            <HStack spacing={1.5}>
                                <Box w="12px" h="12px" borderRadius="sm" bg="purple.50" border="1px solid" borderColor="purple.200" />
                                <Text fontSize="xs" color="var(--fg-muted)">事假/其他</Text>
                            </HStack>
                        </HStack>
                    </Box>
                </Box>
            ) : (
            <>
            {/* 月曆 */}
            <Box flex="1" display="flex" flexDirection="column" border="1.5px solid" borderColor="gray.200" borderRadius="xl" boxShadow="sm" bg="white" overflow="hidden">
                {/* 星期標頭 */}
                <Grid templateColumns="repeat(7, 1fr)" gap={0} bg="gray.50" borderBottom="1.5px solid" borderColor="gray.200">
                    {WEEKDAYS.map((w, idx) => (
                        <GridItem key={w} borderLeft={idx === 0 ? 'none' : '1px solid'} borderColor="gray.100">
                            <Text
                                textAlign="center" fontSize="xs" fontWeight="bold" py={2}
                                color={idx === 0 || idx === 6 ? 'red.300' : 'gray.500'}
                            >
                                {w}
                            </Text>
                        </GridItem>
                    ))}
                </Grid>

                {/* 日期格子 */}
                <Grid flex="1" templateColumns="repeat(7, 1fr)" templateRows={`repeat(${numRows}, 1fr)`} gap={0}>
                    {cells.map((cell, i) => {
                        if (!cell) {
                            const col = i % 7;
                            return (
                                <GridItem key={`empty-${i}`}
                                    borderRight={col < 6 ? '1px solid' : 'none'}
                                    borderBottom="1px solid" borderColor="gray.100"
                                    bg="gray.50"
                                />
                            );
                        }
                        const col = i % 7;
                        const { day, dateStr, isWeekend } = cell;
                        const data = leaveMap[dateStr] || { sick: [], personal: [], other: [] };
                        const sickCount = data.sick.length;
                        const personalCount = data.personal.length;
                        const otherCount = data.other.length;
                        const hasLeaves = sickCount + personalCount + otherCount > 0;
                        const isToday = dateStr === today;
                        const isSelected = selectedDates.has(dateStr);

                        const totalRows = Math.ceil(cells.length / 7);
                        const currentRow = Math.floor(i / 7);
                        const isLastRow = currentRow === totalRows - 1;

                        let cellBg = isWeekend ? 'gray.50' : 'white';
                        if (isToday) cellBg = 'blue.50';
                        if (sickCount > 0) cellBg = isToday ? 'red.100' : 'red.50';
                        else if (hasLeaves) cellBg = isToday ? 'purple.100' : 'purple.50';

                        const hoverBg = sickCount > 0 ? 'red.100' : hasLeaves ? 'purple.100' : isToday ? 'blue.100' : 'gray.50';

                        return (
                            <GridItem key={dateStr}
                                borderRight={col < 6 ? '1px solid' : 'none'}
                                borderBottom={!isLastRow ? '1px solid' : 'none'}
                                borderColor="gray.100"
                                borderTop={isToday ? '3px solid' : '0px'}
                                borderTopColor={isToday ? 'red.400' : 'transparent'}
                            >
                                <Box
                                    position="relative" h="100%" bg={cellBg}
                                    cursor="pointer" _hover={{ bg: hoverBg }}
                                    onClick={() => handleCellClick(dateStr)}
                                    userSelect="none" transition="background 0.15s"
                                >
                                    <Text
                                        pos="absolute" top="6px" left="8px"
                                        fontSize="sm" fontWeight={isToday ? 'bold' : 'normal'}
                                        color={isToday ? 'red.600' : isWeekend ? 'gray.400' : 'gray.700'}
                                    >
                                        {day}
                                    </Text>

                                    {/* 多日選取圓圈 */}
                                    <Box
                                        pos="absolute" top="6px" right="6px"
                                        w="16px" h="16px" borderRadius="full"
                                        border="2px solid"
                                        borderColor={isSelected ? 'red.500' : 'gray.300'}
                                        bg={isSelected ? 'red.500' : 'transparent'}
                                        cursor="pointer"
                                        onClick={(e) => toggleDateSelection(e, dateStr)}
                                        _hover={{ borderColor: 'red.400' }}
                                        zIndex={1}
                                        transition="all 0.15s"
                                    />

                                    <VStack pos="absolute" bottom="5px" left="6px" spacing={0.5} align="flex-start">
                                        {sickCount > 0 && (
                                            <Badge colorScheme="red" fontSize="10px" px={1}>
                                                病假 {sickCount}
                                            </Badge>
                                        )}
                                        {personalCount > 0 && (
                                            <Badge colorScheme="blue" fontSize="10px" px={1}>
                                                事假 {personalCount}
                                            </Badge>
                                        )}
                                        {otherCount > 0 && (
                                            <Badge colorScheme="gray" fontSize="10px" px={1}>
                                                其他 {otherCount}
                                            </Badge>
                                        )}
                                    </VStack>
                                </Box>
                            </GridItem>
                        );
                    })}
                </Grid>
            </Box>

            {/* 圖例 */}
            <Box mt={3} px={1}>
                <HStack spacing={6} flexWrap="wrap" gap={2}>
                    <HStack spacing={1.5}>
                        <Box w="14px" h="14px" borderRadius="sm" bg="red.50" border="1px solid" borderColor="red.200" />
                        <Text fontSize="xs" color="var(--fg-muted)">有病假</Text>
                    </HStack>
                    <HStack spacing={1.5}>
                        <Box w="14px" h="14px" borderRadius="sm" bg="purple.50" border="1px solid" borderColor="purple.200" />
                        <Text fontSize="xs" color="var(--fg-muted)">只有事假/其他</Text>
                    </HStack>
                    <HStack spacing={1.5}>
                        <Box w="14px" h="14px" borderRadius="sm" bg="blue.50" borderTop="3px solid" borderColor="blue.50" borderTopColor="red.400" />
                        <Text fontSize="xs" color="var(--fg-muted)">今日</Text>
                    </HStack>
                    <HStack spacing={1.5}>
                        <Box w="14px" h="14px" borderRadius="full" border="2px solid" borderColor="red.500" bg="red.500" />
                        <Text fontSize="xs" color="var(--fg-muted)">已勾選日期</Text>
                    </HStack>
                    <Text fontSize="xs" color="var(--fg-weak)">・勾選圓圈可跨日選取，再一次登記多天請假</Text>
                </HStack>
            </Box>
            </>
            )}

            {/* 多日選取 Action Bar */}
            {selectedDates.size > 0 && (
                <Box
                    position="fixed"
                    bottom={{ base: 'calc(env(safe-area-inset-bottom) + 78px)', md: 6 }}
                    left="50%" transform="translateX(-50%)"
                    bg="red.500" color="white" px={6} py={3} borderRadius="full"
                    boxShadow="xl" zIndex={100}
                >
                    <HStack spacing={4}>
                        <IconButton
                            icon={<FiX />} size="xs" variant="ghost" color="white"
                            onClick={() => setSelectedDates(new Set())}
                            aria-label="取消選取"
                        />
                        <Text fontWeight="bold" fontSize="sm">
                            已選 {selectedDates.size} 天
                        </Text>
                        <Button size="sm" bg="white" color="red.500" _hover={{ bg: 'red.50' }} onClick={openBatch}>
                            登記請假
                        </Button>
                    </HStack>
                </Box>
            )}

            {/* ── 單日 Modal ── */}
            <Modal isOpen={isDayOpen} onClose={closeDay} size="md" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack justify="space-between" pr={8}>
                            <Text>{dayTarget} 請假名單</Text>
                            <Badge colorScheme="gray">{dayLeaves.length} 人</Badge>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={4}>
                        {dayLeaves.length === 0 ? (
                            <Center py={6}>
                                <Text color="var(--fg-weak)">當日無請假紀錄</Text>
                            </Center>
                        ) : (
                            <VStack spacing={0} align="stretch" divider={<Divider />}>
                                {dayLeaves.map(leave => {
                                    const name = leave.student?.full_name || `學生 #${leave.student_id}`;
                                    return (
                                        <HStack key={leave.id} py={2} justify="space-between">
                                            <HStack spacing={3}>
                                                <Badge colorScheme={TYPE_COLOR[leave.type] || 'gray'} fontSize="xs">
                                                    {TYPE_LABEL[leave.type] || '其他'}
                                                </Badge>
                                                <Text fontWeight="500">{name}</Text>
                                                {leave.reason && (
                                                    <Text fontSize="sm" color="var(--fg-muted)">{leave.reason}</Text>
                                                )}
                                            </HStack>
                                            <IconButton
                                                icon={<FiTrash2 />} size="xs"
                                                colorScheme="red" variant="ghost"
                                                onClick={() => handleDelete(leave.id)}
                                                aria-label="取消請假"
                                            />
                                        </HStack>
                                    );
                                })}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button leftIcon={<FiPlus />} colorScheme="brand" size="sm" onClick={handleOpenAddFromDay}>
                            登記請假
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* ── 搜尋 Modal ── */}
            <Modal isOpen={isSearchOpen} onClose={() => { closeSearch(); setSearchInput(''); setSearchResults([]); }} size="md" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>搜尋學生請假紀錄</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={4}>
                        <HStack mb={4}>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none">
                                    <FiSearch color="gray" />
                                </InputLeftElement>
                                <Input
                                    placeholder="輸入學生姓名..."
                                    value={searchInput}
                                    onChange={e => setSearchInput(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                />
                            </InputGroup>
                            <Button colorScheme="brand" onClick={handleSearch} isLoading={searchLoading} flexShrink={0}>
                                搜尋
                            </Button>
                        </HStack>

                        {searchResults.length === 0 && !searchLoading && searchInput && (
                            <Center py={4}>
                                <Text color="var(--fg-weak)">找不到相關紀錄</Text>
                            </Center>
                        )}

                        {searchResults.length > 0 && (
                            <VStack spacing={0} align="stretch" divider={<Divider />}>
                                {searchResults.map(leave => {
                                    const name = leave.student?.full_name || `學生 #${leave.student_id}`;
                                    return (
                                        <HStack key={leave.id} py={2} justify="space-between">
                                            <HStack spacing={3} flexWrap="wrap">
                                                <Text fontSize="sm" color="var(--fg-muted)" minW="90px">{leave.date}</Text>
                                                <Badge colorScheme={TYPE_COLOR[leave.type] || 'gray'} fontSize="xs">
                                                    {TYPE_LABEL[leave.type] || '其他'}
                                                </Badge>
                                                <Text fontWeight="500">{name}</Text>
                                                {leave.reason && (
                                                    <Text fontSize="sm" color="var(--fg-muted)">{leave.reason}</Text>
                                                )}
                                            </HStack>
                                            <IconButton
                                                icon={<FiTrash2 />} size="xs"
                                                colorScheme="red" variant="ghost"
                                                onClick={() => handleSearchDelete(leave.id)}
                                                aria-label="取消請假"
                                            />
                                        </HStack>
                                    );
                                })}
                            </VStack>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>

            {/* ── 登記請假 Modal ── */}
            <LeaveApplicationModal
                isOpen={isAddOpen}
                onClose={() => closeAdd()}
                onSuccess={handleLeaveSuccess}
                defaultDate={addDefaultDate}
            />

            {/* ── 批量請假 Modal ── */}
            <LeaveBatchModal
                isOpen={isBatchOpen}
                onClose={closeBatch}
                selectedDates={selectedDates}
                onSuccess={() => { setSelectedDates(new Set()); fetchMonthLeaves(); }}
            />
        </Box>
    );
};

export default LeavesPage;
